import { useState } from 'react'

import { MANAGEMENT_QUERY_KEY_SEARCH_UNIT_PLANS, useManagementSearchUnitPlans } from '@apps-common/apis/management/unit-plan'
import { ButtonRoundSm } from '@apps-common/components/elements'
import { Table } from '@apps-common/components/elements/table'
import { LayoutContainer, LayoutPadding } from '@apps-common/components/layout'
import { ModalButton } from '@apps-common/components/modals'
import { commonIcons } from '@apps-common/config/icons'
import { formatCurrency } from '@apps-common/helpers/currency'
import { valueStringCheck } from '@apps-common/helpers/value-string-check'
import { queryClient } from '@apps-common/lib'
import { modalSlideUpEnterAnimation, modalSlideUpLeaveAnimation } from '@apps-common/lib/modal-animations'

import { UnitPlanModal } from './UnitPlanModal'

interface IProps {
  searchVal: string
}

export const UnitPlanTableView: React.FC<IProps> = ({ searchVal }) => {
  const [skip, setSkip] = useState<number>(0)
  const [take, setTake] = useState<number>(10)
  const { isLoading, isError, data } = useManagementSearchUnitPlans({
    search: searchVal
  })

  const keys = [
    {
      title: 'Plan',
      key: 'unitPlanName',
      thClasses: '',
      tdClasses: ''
    },
    {
      title: 'Product Range',
      key: 'productRange',
      thClasses: '',
      tdClasses: ''
    },
    {
      title: 'Status',
      key: 'status',
      thClasses: '',
      tdClasses: ''
    },
    {
      title: 'Upgradeability',
      key: 'upgradePath',
      thClasses: '',
      tdClasses: '',
      Component: (props: any) => <span className='capitalize'>{props.upgradePath}</span>
    },
    {
      title: 'Price',
      key: 'price',
      thClasses: '',
      tdClasses: '',
      Component: (props: any) => <span>{props.price ? formatCurrency(props.price) : valueStringCheck(props.price)}</span>
    },
    {
      title: 'Default',
      key: 'defaultItem',
      thClasses: '',
      tdClasses: '',
      Component: (props: any) => (
        <span>
          {props.defaultItem ? (
            <commonIcons.checkCircle className='text-[1.5em] bg-success rounded-full text-on-success p-[2px]' />
          ) : (
            <commonIcons.close className='text-[1.5em] bg-surface-2 rounded-full text-text p-[2px]' />
          )}
        </span>
      )
    },
    {
      title: 'Actions',
      key: 'actions',
      static: true,
      thClasses: 'text-right',
      tdClasses: '',
      Component: (props: any) => (
        <div className='flex items-center justify-end gap-[5px]'>
          <ModalButton
            component={UnitPlanModal}
            componentProps={{
              unitPlanId: props.unitPlanId
            }}
            confirmCallback={() => {
              queryClient.refetchQueries({ queryKey: MANAGEMENT_QUERY_KEY_SEARCH_UNIT_PLANS(searchVal) })
            }}
            enterAnimation={modalSlideUpEnterAnimation}
            leaveAnimation={modalSlideUpLeaveAnimation}
          >
            <ButtonRoundSm type='div' className='action-brand-solid'>
              <commonIcons.arrowAngled />
            </ButtonRoundSm>
          </ModalButton>
        </div>
      )
    }
  ]

  const loading = isLoading || isError || !data

  return (
    <LayoutContainer>
      <LayoutPadding topPadding='pt-[65px] sm:pt-[25px]'>
        <Table
          loading={loading}
          loadingSkeletonHeight='leading-[30px]'
          placeholderSkeletonHeight='leading-[30px]'
          pagination={{
            loading,
            count: data?.length || 0,
            skip,
            take,
            onSkipChanged: (value: number) => setSkip(value),
            onTakeChanged: (value: number) => setTake(value)
          }}
          keys={keys}
          data={data?.map((c) => ({ ...c, companies: data })) || []}
          sliceData
          topLeftComponent={
            <>
              {!loading && data && (
                <div className='jost-light'>
                  <span className='jost-medium'>{data?.length}</span> plan results
                </div>
              )}
              {(loading || !data) && <div className='h-[22px] w-[100px] bg-secondary-content/5 rounded-lg' />}
            </>
          }
          onSkipChanged={(value: number) => setSkip(value)}
          onTakeChanged={(value: number) => setTake(value)}
        />
      </LayoutPadding>
    </LayoutContainer>
  )
}
